import { FileInspector, type FileInspectorProps } from "@msuite/ui/files";
import { getFileAnnotations, saveFileAnnotations } from "@/api/files";
import { useFilesWithAnnotations } from "@/hooks/use-files-with-annotations";

/**
 * Props of `AnnotatedFileInspector`: the inspector's own, which are a union of its two variants and
 * therefore spelled as an intersection rather than an interface extending them.
 */
type AnnotatedFileInspectorProps<T extends { id: string }> = FileInspectorProps<T> & {
	/**
	 * Whether the annotator offers editing — the parent's answer, from
	 * `useFileCapabilities(parent).canWrite`, because annotating follows the parent the files hang
	 * on. Defaults to `true`, leaving the save to the server, for a caller with no parent to ask.
	 */
	canAnnotate?: boolean;
};

/**
 * Drop-in replacement for `FileInspector` that wires lightbox previews (inline thumbnails
 * or modal cards, whenever an item is an image) to the file annotation capability and the
 * annotation hint badge.
 */
export function AnnotatedFileInspector<T extends { id: string }>({
	canAnnotate = true,
	...props
}: AnnotatedFileInspectorProps<T>) {
	/** Query */
	const fileIds = props.itemFiles.map((item) => item.file.id);
	const { annotatedFileIds } = useFilesWithAnnotations(fileIds);

	/** Render */
	return (
		<FileInspector
			{...(props as FileInspectorProps<T>)}
			getAnnotations={(fileId) => getFileAnnotations(fileId, canAnnotate)}
			onSaveAnnotations={saveFileAnnotations}
			annotatedFileIds={annotatedFileIds}
		/>
	);
}
